import React, { useEffect, useState } from 'react';
import { Card, Table } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import ToggleChip from '../../../shared/components/ToggleChip';
import { NavLink } from 'react-router-dom';
import { IPackages } from '../../../model/package/IPackage';
import { PackagesService } from '../PackagesService';
import { ErrorToast } from '../../../utils/toasters';

const PACKAGE_COLUMNS: ITableColumn[] = [
    { label: 'ID' },
    { label: 'Name' },
    { label: 'Key' },
    { label: 'Color' },
    { label: 'Monthly Price' },
    { label: 'Annual Price' },
    { label: 'Position' },
    { label: 'Active' },
    { label: 'Shown' },
    { label: 'Image' },
    { label: 'Action' },
];

export default function PackagesList() {
    const [pagination, setPagination] = useState({
        page: 1,
        perPage: 15,
    });
    const [totalPages, setTotalPages] = useState(0);
    const [packagesList, setPackagesList] = useState<Partial<IPackages>[]>([]);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        setDataLoading(true);
        PackagesService.getPackages(pagination)
            .then(data => {
                setPackagesList(data.data);
                setTotalPages(Math.ceil(data.count / pagination.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [pagination]);

    const handlePages = (updatePage: number) => {
        setPagination({ ...pagination, page: updatePage });
    };

    function handleActiveCheckbox(id: number) {
        PackagesService.togglePackage(id)
            .then(() => {
                setPackagesList(prev => prev.map(e => (e.id === id ? { ...e, isActive: !e.isActive } : e)));
            })
            .catch(err => ErrorToast(err));
    }

    function handleShownCheckbox(id: number) {
        PackagesService.togglePackageShow(id)
            .then(() => {
                setPackagesList(prev => prev.map(e => (e.id === id ? { ...e, isShownToUser: !e.isShownToUser } : e)));
            })
            .catch(err => ErrorToast(err));
    }

    return (
        <div>
            <NavLink
                className="btn btn-outline-primary"
                to={{
                    pathname: '/packages/form/',
                }}
            >
                + Add package
            </NavLink>
            <Card className="mb-5 p-4 mt-5">
                {dataLoading ? (
                    <TableSkeleton columns={PACKAGE_COLUMNS} rows={pagination.perPage} />
                ) : (
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {PACKAGE_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {packagesList.map(item => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.displayName}</td>
                                    <td>{item.name}</td>
                                    <td>
                                        {item.color ? (
                                            <div className="d-flex align-items-center gap-2">
                                                <span
                                                    className="d-inline-block rounded border flex-shrink-0"
                                                    style={{ width: 24, height: 24, backgroundColor: item.color }}
                                                />
                                                <span className="text-muted">{item.color}</span>
                                            </div>
                                        ) : (
                                            '-'
                                        )}
                                    </td>
                                    <td>{item.monthlyPrice}</td>
                                    <td>{item.annualPrice}</td>
                                    <td>{item.position}</td>
                                    <td>
                                        <ToggleChip
                                            name="isActive"
                                            onChange={() => handleActiveCheckbox(item.id as number)}
                                            checked={!!item.isActive}
                                            label={item.isActive ? 'Active' : 'Inactive'}
                                        />
                                    </td>
                                    <td>
                                        <ToggleChip
                                            name="isShownToUser"
                                            onChange={() => handleShownCheckbox(item.id as number)}
                                            checked={!!item.isShownToUser}
                                            label={item.isShownToUser ? 'Shown' : 'Hidden'}
                                        />
                                    </td>
                                    <td>
                                        <img
                                            alt=""
                                            src={item.image ? item?.image?.thumbnail : '/images/placeholder.png'}
                                            style={{ width: 70, height: 60 }}
                                            className="rounded"
                                        />
                                    </td>
                                    <td>
                                        <NavLink
                                            className="btn btn-primary w-100 text-white"
                                            to={{
                                                pathname: '/packages/form/' + item.id,
                                            }}
                                        >
                                            Edit
                                        </NavLink>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                <Pagination
                    page={pagination.page}
                    totalPages={totalPages}
                    handlePagination={handlePages}
                    disabled={dataLoading}
                />
            </Card>
        </div>
    );
}
